/**
 * The toast shown after create-admin-user succeeds, which depends on whether the welcome email
 * actually went out (`welcome_email_sent`, added 2026-10-15). Until then this said "User created
 * successfully" while every welcome email failed for a month (20261015a6).
 */
export interface UserCreatedToast {
  title: string;
  description: string;
  variant?: 'destructive';
}

export function userCreatedToast(response: { welcome_email_sent?: boolean }): UserCreatedToast {
  // An older deploy of the function sends no flag; it is not evidence of a failure.
  if (response.welcome_email_sent !== false) {
    return {
      title: 'Success',
      description: 'User created. A welcome email with a link to set their password is on its way.',
    };
  }
  return {
    title: 'User created, but the welcome email was not sent',
    description:
      'Unless you gave them a password, ask them to use "Forgot password" on the sign-in page to set one.',
    variant: 'destructive',
  };
}

/** Which email an admin is sending from Settings → Users: the onboarding one, or a plain reset. */
export type AccessEmailKind = 'welcome' | 'reset';

export function accessEmailToast(
  kind: AccessEmailKind,
  email: string,
  emailSent: boolean,
): UserCreatedToast {
  const label = kind === 'welcome' ? 'Welcome email' : 'Password reset email';
  if (!emailSent) {
    return {
      title: `${label} not sent`,
      description: `The link was created but the email to ${email} did not go out. Try again in a few minutes.`,
      variant: 'destructive',
    };
  }
  return {
    title: `${label} sent`,
    description:
      kind === 'welcome'
        ? `${email} has a new link to set their password. It works for 24 hours.`
        : `${email} has a link to choose a new password. It works for 24 hours.`,
  };
}
